import React, { useRef } from 'react'
import PropTypes from 'prop-types'

import defaultAvatar from '../../assets/images/default-avatar.png'

/**
 * ProfileImage Component
 *
 * A React component that displays the user's profile image along with their name and email.
 * If the user does not have a profile image, a default avatar is shown.
 *
 * @module profile/ProfileImage
 * @description A React component that displays the user's profile image along with their name and email.
 * If the user does not have a profile image, a default avatar is shown.
 * @param {Object} authDetails - Authentication details including user information.
 * @param {Object} authDetails.authentication - Authentication details object.
 * @param {Object} authDetails.authentication.user - User details object.
 * @param {Object[]} authDetails.authentication.user.profile.photos - Array of user profile photos.
 * @param {string} authDetails.authentication.user.profile.photos[0].value - URL of the user's profile photo.
 * @param {Object} authDetails.session - Session details object.
 * @param {Object} authDetails.session.user - User details object in session.
 * @param {string} authDetails.session.user.userFullName - Full name of the user.
 * @param {string} authDetails.session.user.email - Email address of the user.
 *
 * @example
 * const authDetails = {
 *   authentication: {
 *     user: {
 *       profile: {
 *         photos: [{ value: 'http://example.com/photo.jpg' }]
 *       }
 *     }
 *   },
 *   session: {
 *     user: {
 *       userFullName: 'John Doe',
 *       email: 'john.doe@example.com'
 *     }
 *   }
 * };
 *
 * return (
 *   <ProfileImage authDetails={authDetails} />
 * );
 *
 * @returns {React.Element} The rendered component with the user's profile image, name, and email.
 */
export const ProfileImage = ({ authDetails }) => {
  const profileImgRef = useRef()
  const onProfileImageError = () => (profileImgRef.current.src = defaultAvatar)
  return (
    <div className="trtui-card trtui-mt-n5">
      <div className="trtui-card-body trtui-p-4">
        <div className="trtui-text-center">
          <div className="trtui-profile-user position-relative trtui-d-inline-block trtui-mx-auto trtui-mb-4">
            <img
              src={authDetails.authentication.user.profile.photos[0].value}
              ref={profileImgRef}
              onError={onProfileImageError}
              className="trtui-rounded-circle trtui-avatar-xl trtui-img-thumbnail trtui-user-profile-image"
              alt="user-profile-pic"
            />
          </div>
          <h5 className="trtui-fs-16 trtui-mb-1">{authDetails?.session?.user.userFullName}</h5>
          <p className="trtui-text-muted trtui-mb-0">{authDetails?.session?.user.email}</p>
        </div>
      </div>
    </div>
  )
}

ProfileImage.propTypes = {
  authDetails: PropTypes.object.isRequired,
}
